<template>
  <div class="wrapper-double-content">
    <div class="row flex-grow-1">
      <div class="col d-flex flex-grow-1">
        <main
          class="wrapper-double-content--main-wrapper-content"
          :class="displayNav && !breadcrumb ? 'nav-space' : ''"
        >
          <div class="portal-content--bread-crumbs bread-crumbs d-flex align-items-center" v-if="breadcrumb">
            <b-button class="crumb" variant="link" :to="breadcrumb.path">
              <font-awesome-icon
                class="mr-2"
                :icon="['far', 'arrow-left']"
              />{{ breadcrumb.text }}
            </b-button>
          </div>
          <router-view
            class="main-content"
            :conference="conference"
            :approval-trees="approvalsTrees"
            :conference-loading="conferenceLoading"
            :approval-trees-loading="approvalsTreesLoading"
          />
        </main>
        <ifac-collapsible-side-panel
          :iconList="iconList"
          @icon-click="handleIconClick"
          :headerText="headerText"
        >
          <keep-alive>
            <component
              :is="activeComponent.component"
              v-bind="activeComponent.options"
              @update-revision="updateConferenceRevision"
            />
          </keep-alive>
        </ifac-collapsible-side-panel>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable vue/no-unused-components */
import { mapMutations, mapState } from 'vuex';
import {
  IfacCollapsibleSidePanel,
  IfacApprovalComments,
  IfacConferenceData,
  IfacConferenceChanges,
  ifacApprovalTreeMixin,
} from '@ifac/ui';
import Conferences from '@/services/Api/Conferences';
import Revisions from '@/services/Api/Conference/Revisions';

export default {
  name: 'PortalContent',
  mixins: [ifacApprovalTreeMixin],
  components: {
    IfacCollapsibleSidePanel,
    IfacApprovalComments,
    IfacConferenceData,
    IfacConferenceChanges,
  },
  data() {
    return {
      sidePanelView: 0,
      approvalListOptions: [],
      conference: null,
      conferenceRevisions: null,
      approvalsTrees: null,
      approvalsTreesLoading: false,
      conferenceLoading: false,
    };
  },
  computed: {
    ...mapState({
      sidePanelIsCollapsed: (state) => state.uiHelpers.sidePanelIsCollapsed,
      speechBubbleDisabled: (state) => state.uiHelpers.speechBubbleDisabled,
    }),
    breadcrumb() {
      return this.$route.meta.breadcrumbParent ?? null;
    },
    displayNav() {
      return ['PostRegister', 'ConferencesApply']
        .includes(this.$route.name);
    },
    headerText() {
      return [
        'Application',
        'Comments and recommendations',
        'Application modifications',
      ][this.sidePanelView];
    },
    iconList() {
      const iconList = [{ icon: ['far', 'tasks'] }];

      if (!this.speechBubbleDisabled) {
        iconList.push({ icon: ['far', 'comment'] });
      }

      if (this.canViewConferenceChanges) {
        iconList.push({ icon: ['fas', 'circle-exclamation'] });
      }

      return iconList;
    },
    conferenceId() {
      return this.$route?.params?.conferenceId || '';
    },
    sidePanelComponents() {
      const panels = [
        {
          component: IfacConferenceData,
          options: {
            data: this.conference,
            isLoading: this.conferenceLoading,
          },
        },
        {
          component: IfacApprovalComments,
          options: {
            data: this.approvalsTrees,
            isLoading: this.approvalsTreesLoading,
            expandSectionsByDefault: true,
            expandHierarchyTreeByDefault: true,
            expandNestedHierarchyByDefault: true,
          },
        },
      ];

      if (this.canViewConferenceChanges) {
        panels.push({
          component: IfacConferenceChanges,
          options: {
            conference: this.conference,
            revisions: this.conferenceRevisions,
            isLoading: this.conferenceLoading,
          },
        });
      }

      return panels;
    },
    activeComponent() {
      return this.sidePanelComponents[this.sidePanelView];
    },
    canViewConferenceChanges() {
      if (this.mx_isCbLiaison === true) {
        return true;
      }

      if (this.mx_isTbChair === true && this.conferenceRevisions) {
        const lastRevision = this.conferenceRevisions[0];
        if (lastRevision && lastRevision.review_status === 'rejected') {
          return true;
        }
      }

      return false;
    },
  },
  created() {
    this.fetchConference();
    this.fetchApprovalsTrees();
    this.fetchConferenceChanges();
  },
  methods: {
    ...mapMutations({
      toggleSidePanelCollapse: 'uiHelpers/toggleSidePanelCollapse',
    }),
    handleIconClick(index) {
      if (this.sidePanelIsCollapsed) this.toggleSidePanelCollapse();
      this.sidePanelView = index;
    },
    async fetchConference() {
      this.conferenceLoading = true;
      try {
        const response = await Conferences.get(this.conferenceId);
        const conferenceData = response?.data?.data;

        if (response?.status === 200 && conferenceData) {
          this.conference = conferenceData;
        }
      } catch (error) {
        console.log(error);
      }
      this.conferenceLoading = false;
    },
    async fetchApprovalsTrees() {
      this.approvalsTreesLoading = true;
      try {
        const response = await Conferences.approvalTree(this.conferenceId);
        const approvalsTrees = response?.data?.data;

        if (response?.status === 200 && approvalsTrees) {
          this.approvalsTrees = approvalsTrees.reverse();
          this.setApprovalData(this.approvalsTrees);
        }
      } catch (error) {
        console.log(error);
      }
      this.approvalsTreesLoading = false;
    },
    async fetchConferenceChanges() {
      try {
        const response = await Revisions.all(this.conferenceId);
        const data = response?.data?.data;

        if (response?.status === 200 && data) {
          this.conferenceRevisions = data;
        }
      } catch (error) {
        console.log(error);
      }
    },
    async updateConferenceRevision(payload) {
      try {
        const response = await Revisions.update(this.conference.id, payload);
        this.conferenceRevisions = [response?.data?.data];
      } catch (error) {
        console.log(error);
      }
    },
    setApprovalData(approvalsTrees) {
      this.mx_breakdownTreeNodes(approvalsTrees);
      this.mx_setInitialData();
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/base/_variables.scss";
@import "@/assets/styles/base/_mixins.scss";

.wrapper-double-content {
  display: flex;
  position: relative;
  padding: 0;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
  margin-left: auto;
  margin-right: auto;

  &--main-wrapper-content {
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    padding-left: 0;
    padding-right: 0;

    .main-content {
      @include media(tablet) {
        padding-right: 2.5rem;
      }
    }
  }
}

.portal-content {
  &--bread-crumbs {
    margin-bottom: 0;
  }
}
</style>
